/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React from 'react';
import { motion } from 'motion/react';
import { Logo } from './Logo';

interface LoadingScreenProps {
  variant?: 'emerald' | 'rose';
}

export const LoadingScreen = ({ variant = 'emerald' }: LoadingScreenProps) => (
  <div className="fixed inset-0 bg-zinc-950 flex flex-col items-center justify-center z-50 select-none pointer-events-none">
    <div className="relative flex flex-col items-center justify-center" style={{ perspective: 1000 }}>
      {/* Ambient background glow */}
      <div
        className={`absolute w-36 h-36 rounded-full blur-3xl pointer-events-none -z-10 ${
          variant === 'rose' ? 'bg-rose-500/15' : 'bg-emerald-500/15'
        }`}
      />

      {/* Hovering wrapper (floating up and down) */}
      <motion.div
        animate={{ y: [-8, 8, -8] }}
        transition={{
          duration: 2.2,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
        className="flex flex-col items-center justify-center"
      >
        {/* Y-axis rotation wrapper */}
        <motion.div
          animate={{ rotateY: 360 }}
          transition={{
            duration: 2.4,
            repeat: Infinity,
            ease: 'linear',
          }}
          style={{ transformStyle: 'preserve-3d' }}
          className="flex items-center justify-center"
        >
          <Logo size={64} className="drop-shadow-[0_10px_25px_rgba(0,0,0,0.6)]" />
        </motion.div>
      </motion.div>

      {/* Floor shadow expanding and contracting with vertical hover */}
      <motion.div
        animate={{
          scale: [0.8, 1.15, 0.8],
          opacity: [0.25, 0.5, 0.25],
        }}
        transition={{
          duration: 2.2,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
        className="w-14 h-3 bg-black/70 rounded-full blur-[3px] mt-6 pointer-events-none"
      />
    </div>
  </div>
);
